import {
  buildEncodedWhatsAppOrderText,
  decodeWhatsAppEncodedText,
} from '@/lib/checkout/whatsappOrder';
import { parseCompositeOrderText } from '@/lib/assistant/parseCompositeOrderText';
import { bestCompositeProductMatch } from '@/lib/assistant/assistantProductSearch';
import { normalizeAssistantText } from '@/lib/assistant/assistantTextUtils';
import {
  extractAddressFromLines,
  findAddressInStaffLines,
  findTrailingAddressInText,
  looksLikeAddressLine,
  looksLikeComunaLine,
  looksLikeStreetNumberLine,
  parseAddressFromLine,
} from '@/lib/orders/orderAddressParse';
import { selectionToCanonicalPayment } from '@/lib/checkout/paymentMethods';
import { promoCatalogItemToCartProduct } from '@/lib/menu/catalogPromos';

const PHONE_RX =
  /(?:\+?\s*56\s*)?(?:9\s*\d{4}\s*\d{4}|569\d{8}|9\d{8})\b|\+569\d{8}/;

function stripPhones(text) {
  return String(text || '').replace(PHONE_RX, '').trim();
}

function lineIsPhoneOnly(line) {
  return Boolean(String(line || '').trim()) && stripPhones(line).length < 4;
}

export const ORDER_LINE_HINT_RX =
  /\b(pago|efectivo|transfer|debito|débito|tarjeta|delivery|retiro|mesa|lata|latas|bebida|bebidas|bigmati|big|smash|burger|hamburguesa|kids|nugget|papas|combo|classic|grill|gaseosa|schop|cerveza|extra|carne|doble|simple|pizza|piezas|pieza|familiar|grupo|sin\s+)/i;

function joinAddressParts(street, comuna) {
  const st = String(street || '').trim();
  const cm = String(comuna || '').trim();
  if (!st) return '';
  return cm ? `${st}, ${cm}` : st;
}

/** Dirección vs producto: solo descarta si el texto coincide exactamente con un producto de la carta. */
function staffLineLooksLikeAddress(line, products = null) {
  if (!looksLikeAddressLine(line)) return false;
  if (!products?.length) return true;
  const qn = normalizeAssistantText(line);
  const exactProduct = products.some((p) => normalizeAssistantText(p?.name || '') === qn);
  if (exactProduct) return false;
  if (looksLikeStreetNumberLine(line)) return true;
  return !bestCompositeProductMatch(line, products);
}

function parseStaffAddressLine(line, products = null) {
  if (!staffLineLooksLikeAddress(line, products)) return null;
  return parseAddressFromLine(line);
}

function isProbableCustomerNameLine(line, { allowLong = true } = {}) {
  const s = String(line || '').trim();
  if (!s || s.length > 40 || /\d/.test(s)) return false;
  if (!/^[a-záéíóúñ]+(\s+[a-záéíóúñ]+){0,2}$/i.test(s)) return false;
  if (ORDER_LINE_HINT_RX.test(s) || looksLikeStreetNumberLine(s)) return false;
  const parsed = parseAddressFromLine(s);
  if (parsed?.street && parsed?.comuna) return false;
  if (!allowLong && s.length > 6) return false;
  return s.split(/\s+/).length <= 2;
}

function capitalizeNameLine(line) {
  return String(line || '')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Extrae nombre, teléfono, dirección y líneas de producto de un pegado multilínea del staff. */
export function extractStaffOrderMeta(text, products = null) {
  const raw = String(text || '').trim();
  if (!raw) {
    return { orderText: '', customerName: '', customerPhone: '', address: '' };
  }

  let customerPhone = '';
  const phoneMatch = raw.match(PHONE_RX);
  if (phoneMatch?.[0]) {
    customerPhone = phoneMatch[0].replace(/\s/g, '');
    if (!customerPhone.startsWith('+')) {
      if (customerPhone.startsWith('569')) customerPhone = `+${customerPhone}`;
      else if (customerPhone.startsWith('9')) customerPhone = `+56${customerPhone}`;
      else customerPhone = `+${customerPhone}`;
    }
  }

  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let customerName = '';
  const nameLineIdx = new Set();
  if (lines.length && isProbableCustomerNameLine(lines[0], { allowLong: false })) {
    customerName = capitalizeNameLine(lines[0]);
    nameLineIdx.add(0);
  }

  const addressLines = lines.filter((_, i) => !nameLineIdx.has(i));
  let address = '';
  const excludedIdx = new Set(nameLineIdx);
  const located = findAddressInStaffLines(addressLines);
  if (located.address) {
    address = located.address;
    for (const idx of located.usedIndices) {
      const originalIdx = lines.indexOf(addressLines[idx]);
      if (originalIdx >= 0) excludedIdx.add(originalIdx);
    }
  }

  if (!address) {
    const bulk = extractAddressFromLines(addressLines.filter((_, i) => !located.usedIndices.has(i)));
    if (bulk.address) {
      address = bulk.address;
      for (let i = 0; i < addressLines.length; i += 1) {
        if (located.usedIndices.has(i)) continue;
        const parsed = parseAddressFromLine(addressLines[i]);
        if (parsed?.street && parsed.comuna && parsed.full === bulk.address) {
          const originalIdx = lines.indexOf(addressLines[i]);
          if (originalIdx >= 0) excludedIdx.add(originalIdx);
        }
      }
    }
  } else {
    for (let i = 0; i < lines.length; i += 1) {
      if (excludedIdx.has(i)) continue;
      const parsed = parseAddressFromLine(lines[i]);
      if (parsed?.full === address) excludedIdx.add(i);
    }
  }

  const workingLines = lines.filter((_, i) => !excludedIdx.has(i));

  const productLines = [];
  let pendingStreet = '';

  for (const line of workingLines) {
    const strippedPhone = stripPhones(line);
    if (!strippedPhone) continue;

    if (lineIsPhoneOnly(line)) continue;

    if (/^(transferencia|transfer|efectivo|debito|débito|tarjeta|mercadopago)$/i.test(strippedPhone)) {
      productLines.push(`pago ${strippedPhone}`);
      continue;
    }

    const addr = parseStaffAddressLine(strippedPhone, products);
    if (addr?.street && addr.comuna) {
      address = addr.full;
      pendingStreet = '';
      continue;
    }
    if (addr?.street && !addr.comuna) {
      pendingStreet = addr.street;
      continue;
    }
    if (staffLineLooksLikeAddress(strippedPhone, products)) {
      if (looksLikeStreetNumberLine(strippedPhone)) {
        pendingStreet = strippedPhone;
        continue;
      }
    }
    if (pendingStreet && looksLikeComunaLine(strippedPhone)) {
      address = joinAddressParts(pendingStreet, strippedPhone);
      pendingStreet = '';
      continue;
    }
    if (pendingStreet) {
      if (!staffLineLooksLikeAddress(pendingStreet, products)) {
        productLines.push(pendingStreet);
      }
      pendingStreet = '';
    }

    if (/^\d{1,2}\s+\S/.test(strippedPhone) || ORDER_LINE_HINT_RX.test(strippedPhone)) {
      productLines.push(strippedPhone);
      continue;
    }

    if (isProbableCustomerNameLine(strippedPhone)) {
      if (!customerName) customerName = capitalizeNameLine(strippedPhone);
      continue;
    }

    if (staffLineLooksLikeAddress(strippedPhone, products)) {
      const parsed = parseAddressFromLine(strippedPhone);
      if (parsed?.full) {
        address = parsed.full;
        continue;
      }
    }

    productLines.push(strippedPhone);
  }

  if (pendingStreet && !address) {
    const parsed = parseAddressFromLine(pendingStreet);
    if (parsed?.full) {
      address = parsed.full;
    } else if (staffLineLooksLikeAddress(pendingStreet, products)) {
      address = pendingStreet;
    } else {
      productLines.push(pendingStreet);
    }
  }

  if (!address) {
    const trailing = findTrailingAddressInText(stripPhones(raw));
    if (trailing?.full) address = trailing.full;
  }

  return {
    orderText: productLines.join('\n'),
    customerName,
    customerPhone,
    address,
  };
}

export function compositeLinesToCartItems(lines) {
  return (lines || []).map(({ product, qty, removedIngredients, note, addOns }) => {
    const resolved = product?.is_catalog_promo ? promoCatalogItemToCartProduct(product) : product;
    return {
      product: resolved,
      quantity: Math.max(1, Number(qty) || 1),
      price: Number(resolved?.final_price ?? resolved?.price) || 0,
      removedIngredients: removedIngredients || [],
      addOns: addOns || [],
      note: note || null,
    };
  });
}

/** Precio unitario (base + extras). En take-order `line.price` es solo el producto. */
export function staffCartLineUnitPrice(line) {
  const base = Number(line?.price) || 0;
  const extras = (line?.addOns || []).reduce(
    (addonSum, addon) =>
      addonSum + (Number(addon.price) || 0) * Math.max(1, Number(addon.quantity) || 1),
    0,
  );
  return base + extras;
}

export function cartItemsSubtotal(cartItems) {
  return (cartItems || []).reduce((sum, item) => {
    const qty = Math.max(1, Number(item.quantity) || 1);
    return sum + staffCartLineUnitPrice(item) * qty;
  }, 0);
}

export function mapPaymentSelectionToMethod(selection) {
  if (!selection) return 'efectivo';
  const raw = String(selection).toLowerCase();
  if (raw.includes('transfer')) return 'transferencia';
  if (raw.includes('debit') || raw.includes('tarj') || raw.includes('card') || raw.includes('online')) {
    return 'online_manual';
  }
  if (raw.includes('efect') || raw.includes('cash')) return 'efectivo';
  const { canonical } = selectionToCanonicalPayment(selection);
  if (canonical === 'transferencia') return 'transferencia';
  if (canonical === 'online_manual') return 'online_manual';
  return 'efectivo';
}

/**
 * @param {object} params
 * @returns {{ lines: object[], paymentSelection: string|null, address: string|null, cashAmount: number|null, unmatchedNotes: string[], cartItems: object[], subtotal: number, customerName: string, customerPhone: string }|null}
 */
export function parseStaffTakeOrderText(text, products, menuData = null) {
  const { orderText, customerName, customerPhone, address: metaAddress } = extractStaffOrderMeta(
    text,
    products,
  );
  const parsed = parseCompositeOrderText(orderText || text, products, menuData, { staffOrder: true });
  if (!parsed) {
    if (customerName || customerPhone || metaAddress) {
      return {
        lines: [],
        paymentSelection: null,
        address: metaAddress || null,
        cashAmount: null,
        unmatchedNotes: [],
        cartItems: [],
        subtotal: 0,
        customerName,
        customerPhone,
      };
    }
    return null;
  }
  const cartItems = compositeLinesToCartItems(parsed.lines);
  return {
    ...parsed,
    address:
      parsed.address ||
      metaAddress ||
      findTrailingAddressInText(orderText)?.full ||
      findTrailingAddressInText(text)?.full ||
      null,
    cartItems,
    subtotal: cartItemsSubtotal(cartItems),
    customerName,
    customerPhone,
  };
}

export function buildStaffTakeOrderWhatsAppText({
  business,
  cartItems,
  customerName = '',
  customerPhone = '',
  serviceType = 'delivery',
  address = '',
  markerPosition = null,
  orderNotes = '',
  paymentMethod = 'efectivo',
  cashAmount = '',
  deliveryFee = 0,
}) {
  const deliveryQuote = Number(deliveryFee) > 0 ? { delivery_fee: Number(deliveryFee) } : null;
  const encoded = buildEncodedWhatsAppOrderText({
    business,
    cart: cartItems,
    getCartTotal: () => cartItemsSubtotal(cartItems),
    customerName,
    customerPhone,
    serviceType,
    address,
    markerPosition,
    orderNotes,
    paymentMethod,
    cashAmount,
    deliveryQuote,
  });
  return decodeWhatsAppEncodedText(encoded);
}

/** Detecta pegados multilínea estilo Tomar pedido (dashboard). */
export function looksLikeStaffOrderPaste(text) {
  const raw = String(text || '').trim();
  if (!raw) return false;
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  const hasPhone = PHONE_RX.test(raw);
  const hasProductLine = lines.some(
    (l) => /^\d{1,2}\s+\S/.test(l) || ORDER_LINE_HINT_RX.test(l)
  );
  const hasPayment = lines.some(
    (l) => /^pago\s/i.test(l) || /^(efectivo|transferencia|transfer|debito|débito|tarjeta)$/i.test(l)
  );
  const hasNameLike = lines.some(
    (l) => /^[a-záéíóúñ]+(\s+[a-záéíóúñ]+){0,2}$/i.test(l) && l.length <= 40 && !/^\d/.test(l)
  );
  return hasProductLine && (hasPhone || hasPayment || hasNameLike || lines.length >= 4);
}

/** Prefill de checkout del asistente a partir del parse staff. */
export function staffParsedToCheckoutPrefill(parsed) {
  if (!parsed) return {};
  const prefill = {};
  if (parsed.customerName) prefill.customerName = parsed.customerName;
  if (parsed.customerPhone) prefill.customerPhone = parsed.customerPhone;
  if (parsed.address) {
    prefill.address = parsed.address;
    prefill.serviceType = 'delivery';
  }
  if (parsed.paymentSelection) {
    const ps = String(parsed.paymentSelection).toLowerCase();
    if (ps.includes('transfer')) prefill.paymentMethod = 'transferencia';
    else if (ps.includes('efectivo') || ps.includes('cash')) prefill.paymentMethod = 'efectivo';
    else prefill.paymentMethod = mapPaymentSelectionToMethod(parsed.paymentSelection);
  }
  if (parsed.cashAmount != null) prefill.cash_amount = parsed.cashAmount;
  return prefill;
}
